// Helper utility functions

import { FeaturePermissions } from '@/lib/sdk/auth/types';
import { FeatureAccess } from '@/lib/sdk/organization/types';

export const helpers = {
  // Truncate text with ellipsis
  truncate: (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  },

  // Get initials from name
  getInitials: (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  },

  // Deep clone object
  deepClone: <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
  },

  // Check if object is empty
  isEmpty: (obj: object): boolean => {
    return Object.keys(obj).length === 0;
  },

  // Capitalize first letter
  capitalize: (str: string): string => {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
  },

  // Format number with commas
  formatNumber: (num: number): string => {
    return num.toLocaleString();
  },

  // Generate random ID (for temporary UI purposes)
  generateId: (): string => {
    return Math.random().toString(36).substring(2, 9);
  },

  // Sleep utility for delays
  sleep: (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  },

  // Build route with params
  buildRoute: (route: string, params: Record<string, string>): string => {
    let result = route;
    Object.entries(params).forEach(([key, value]) => {
      result = result.replace(`:${key}`, value);
    });
    return result;
  },

  // Check if user has permission — read and read_write are treated as the same "user" level
  hasPermission: (userPermission: string, requiredPermission: string): boolean => {
    const normalize = (p: string) => (p === 'read' || p === 'read_write') ? 'user' : p;
    const levels = ['user', 'admin'];
    return levels.indexOf(normalize(userPermission)) >= levels.indexOf(normalize(requiredPermission));
  },

  // Check if organization has access to a feature
  hasFeatureAccess: (orgFeatureAccess: string[] | undefined, requiredFeature: FeatureAccess): boolean => {
    if (!orgFeatureAccess || orgFeatureAccess.length === 0) return false;
    return orgFeatureAccess.includes(requiredFeature);
  },

  // Validate phone number format
  validatePhoneNumber: (phone: string): boolean => {
    return /^\d{6,15}$/.test(phone);
  },

  // Validate OTP format
  validateOTP: (otp: string): boolean => {
    return /^\d{6}$/.test(otp);
  },

  // Check if user has a specific action on a feature
  hasFeatureAction: (
    featurePermissions: FeaturePermissions | null | undefined,
    userPermission: string | null,
    isSuperAdmin: boolean,
    feature: keyof FeaturePermissions,
    action: string
  ): boolean => {
    if (isSuperAdmin) return true;
    if (!featurePermissions) return false;
    const actions = featurePermissions[feature];
    if (!actions || actions.length === 0) return false;
    return (actions as string[]).includes(action);
  },

  // Check if user has any access to a feature (view or any action)
  hasFeature: (
    featurePermissions: FeaturePermissions | null | undefined,
    userPermission: string | null,
    isSuperAdmin: boolean,
    feature: keyof FeaturePermissions
  ): boolean => {
    if (isSuperAdmin) return true;
    if (!featurePermissions) return false;
    const actions = featurePermissions[feature];
    return !!actions && actions.length > 0;
  },
};
